/**
 * Pantalla de bienvenida con instrucciones y estructura del test
 * 
 * @param {Function} onStart - Callback para iniciar el test
 * @author Ana Lizcano
 */

import React from 'react';
import PropTypes from 'prop-types';
import { styled } from "@mui/material/styles";
import { CheckCircle, Clock, Users, FileText } from 'lucide-react';
import { getTotalQuestions, getQuestionsByBlock } from '../data/lazyQuestions';
import { TEST_CONFIG } from '../config/testConfig';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { COLORS } from '../styles/commonStyles';

// Styled Components
const WelcomeContainer = styled(Card)(({ theme }) => ({
  maxWidth: '900px',
  margin: '0 auto',
  padding: theme.spacing(4),
  borderRadius: '20px',
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)',
  border: `1px solid ${COLORS.border}`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
}));

const HeroSection = styled(MDBox)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));

const IconContainer = styled(MDBox)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
}));

const InstructionItem = styled(MDBox)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: '12px',
  backgroundColor: 'rgba(255,255,255,0.5)',
  border: '1px solid rgba(102, 126, 234, 0.1)',
}));

const BlockCard = styled(Card)(({ theme, blockcolor }) => ({
  padding: theme.spacing(2.5),
  borderRadius: '16px',
  border: `2px solid ${blockcolor}20`,
  backgroundColor: `${blockcolor}08`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${blockcolor}20`,
    backgroundColor: `${blockcolor}12`,
  },
}));

const TipsCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(248, 250, 252, 0.8)',
  border: `1px solid ${COLORS.border}`,
  borderRadius: '16px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const StartButton = styled(MDButton)(({ theme }) => ({
  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
  borderRadius: '25px',
  padding: theme.spacing(1.5, 4),
  fontSize: '16px',
  fontWeight: 'bold',
  textTransform: 'none',
  color: 'white',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${COLORS.primary}dd 0%, ${COLORS.secondary}dd 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
    color: 'white',
  },
}));

const WelcomeScreen = ({ onStart }) => {
  const blockQuestions = getQuestionsByBlock();
  
  const blockColors = {
    A: COLORS.primary,
    B: COLORS.success,
    C: COLORS.secondary, 
    D: COLORS.warning
  };

  return (
    <WelcomeContainer>
      <HeroSection>
        <IconContainer>
          <Users size={40} color="white" />
        </IconContainer>
        <MDTypography variant="h3" fontWeight="bold" color="dark" mb={1}>
          ¡Bienvenido/a al Test de Evaluación!
        </MDTypography>
        <MDTypography variant="h6" color="text" fontWeight="regular">
          Gracias por participar en nuestro proceso de selección
        </MDTypography>
      </HeroSection>

      <MDBox mb={4}>
        <MDTypography variant="h5" fontWeight="bold" color="dark" mb={3}>
          Instrucciones del Test
        </MDTypography>
        
        <InstructionItem>
          <CheckCircle size={20} color={COLORS.success} style={{ marginTop: '2px', flexShrink: 0 }} />
          <MDTypography variant="body1" color="text">
            El test consta de <strong>{getTotalQuestions()} preguntas</strong> divididas en 4 bloques diferentes
          </MDTypography>
        </InstructionItem>
        
        <InstructionItem>
          <Clock size={20} color={COLORS.info} style={{ marginTop: '2px', flexShrink: 0 }} />
          <MDTypography variant="body1" color="text">
            Tiempo límite: <strong>{TEST_CONFIG.TEST_DURATION_MINUTES} minutos</strong>. El test se cerrará automáticamente cuando el tiempo expire
          </MDTypography>
        </InstructionItem>
        
        <InstructionItem>
          <FileText size={20} color={COLORS.secondary} style={{ marginTop: '2px', flexShrink: 0 }} />
          <MDTypography variant="body1" color="text">
            Cada bloque tiene un <strong>formato diferente</strong> de respuesta que se explicará en su momento
          </MDTypography>
        </InstructionItem>
        
        <InstructionItem>
          <CheckCircle size={20} color={COLORS.success} style={{ marginTop: '2px', flexShrink: 0 }} />
          <MDTypography variant="body1" color="text">
            <strong>No hay respuestas correctas o incorrectas</strong>, solo responde con honestidad
          </MDTypography>
        </InstructionItem>
      </MDBox>

      <TipsCard>
        <MDTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
          Consejos para el Test
        </MDTypography>
        <MDBox component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <MDBox component="li" mb={1}>
            <MDTypography variant="body2" color="text">
              • Responde de manera espontánea y honesta
            </MDTypography>
          </MDBox>
          <MDBox component="li" mb={1}>
            <MDTypography variant="body2" color="text">
              • Si tienes dudas, elige la opción que más se acerque a tu sentir
            </MDTypography>
          </MDBox>
          <MDBox component="li" mb={1}>
            <MDTypography variant="body2" color="text">
              • Puedes navegar hacia atrás para revisar tus respuestas
            </MDTypography>
          </MDBox>
          <MDBox component="li">
            <MDTypography variant="body2" color="text">
              • Al final podrás compartir tu experiencia con el test
            </MDTypography>
          </MDBox>
        </MDBox>
      </TipsCard>

      <MDBox textAlign="center">
        <StartButton onClick={onStart} variant="gradient">
          Comenzar Test →
        </StartButton>
      </MDBox>
    </WelcomeContainer>
  );
};

WelcomeScreen.propTypes = {
  onStart: PropTypes.func.isRequired
};

export default WelcomeScreen;